import React from 'react';
import { ArrowPathIcon, ShieldExclamationIcon } from '@heroicons/react/24/solid';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';

export interface NetworkRestrictionPageProps {
  formUrl?: string;
  onRetry: () => void;
  isRetrying: boolean;
  retryFailed: boolean;
  onFormOpen: () => void;
}

// Only https links are rendered so a misconfigured value (javascript:, http:) never becomes a clickable href.
const isHttpsUrl = (value?: string): value is string => {
  if (!value) return false;
  try {
    return new URL(value).protocol === 'https:';
  } catch {
    return false;
  }
};

const FOCUS_RING = 'focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2';

const NetworkRestrictionPage = ({
  formUrl,
  onRetry,
  isRetrying,
  retryFailed,
  onFormOpen,
}: NetworkRestrictionPageProps) => (
  <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50 p-4">
    <div className="w-full max-w-2xl">
      <div className="rounded-2xl bg-white p-8 shadow-2xl md:p-12">
        <div className="mx-auto mb-8 flex h-24 w-24 items-center justify-center rounded-full bg-gradient-to-br from-blue-400 to-indigo-600 shadow-lg">
          <ShieldExclamationIcon className="h-12 w-12 text-white" aria-hidden="true" />
        </div>

        <h1 className="mb-4 text-center text-3xl font-bold text-gray-800 sm:text-4xl">
          IoT Portal access has network restrictions
        </h1>

        <p className="mb-10 text-center text-base leading-relaxed text-gray-600">
          Access to the IoT Portal is limited to the Nestlé network. Your current network is not allowed.
        </p>

        <div className="space-y-8">
          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-gray-800">Connect to the Nestlé network</h2>
            <p className="text-gray-600">Connect to the Nestlé office network or VPN and try again.</p>
            <button
              type="button"
              onClick={onRetry}
              disabled={isRetrying}
              className={`flex w-full items-center justify-center gap-2 rounded-xl bg-indigo-600 px-8 py-3 font-semibold text-white shadow-lg hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60 ${FOCUS_RING}`}
            >
              {isRetrying && <ArrowPathIcon className="h-5 w-5 animate-spin" aria-hidden="true" />}
              {isRetrying ? 'Checking…' : 'Try again'}
            </button>
            {retryFailed && (
              <p role="status" className="text-sm text-red-600">
                Still restricted. Check that the VPN is connected, then try again.
              </p>
            )}
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-gray-800">Request access for your network</h2>
            <p className="text-gray-600">Request that your IP address or range is whitelisted.</p>
            {isHttpsUrl(formUrl) && (
              <a
                href={formUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={onFormOpen}
                className={`flex w-full items-center justify-center gap-2 rounded-xl border border-indigo-600 px-8 py-3 font-semibold text-indigo-600 hover:bg-indigo-50 ${FOCUS_RING}`}
              >
                Open the whitelist request form
                <ArrowTopRightOnSquareIcon className="h-[18px] w-[18px]" aria-hidden="true" />
              </a>
            )}
            <p className="text-sm text-gray-500">
              Partners and vendors: you need a Nestlé guest account to open the form. Ask your Nestlé sponsor first.
            </p>
          </section>
        </div>
      </div>
    </div>
  </div>
);

export default NetworkRestrictionPage;
